import { useTranslation } from '@thoughtspot/i18n';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Checkbox, useCheckboxState, } from '@thoughtspot/radiant-react/widgets/checkbox';
import { Divider } from '@thoughtspot/radiant-react/widgets/divider';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { Horizontal, Vertical, View, } from '@thoughtspot/radiant-react/widgets/layout';
import { LoadingIndicator } from '@thoughtspot/radiant-react/widgets/loading-indicator';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { Radio } from '@thoughtspot/radiant-react/widgets/radio';
import { SafeHTML } from '@thoughtspot/radiant-react/widgets/safe-html';
import { SearchBar } from '@thoughtspot/radiant-react/widgets/search-bar';
import { TextArea } from '@thoughtspot/radiant-react/widgets/text-area';
import { Toggle } from '@thoughtspot/radiant-react/widgets/toggle';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { getAdminGroupGuid, getTSAdminUserGuid, getUserEmail, getUserGuid, isRolesEnabled, isSaaSCluster, isWelcomeEmailEnabled, } from '@thoughtspot/session-service';
import cx from 'classnames';
import _ from 'lodash';
import React, { createRef, Fragment, useCallback, useEffect, useRef, useState, } from 'react';
import { getLabelWithOrWithOutErrorIcon, renderCheckboxListReview, renderFormFieldValue, SHARING_VISIBILITY, } from '../../util';
import { createEditTabLinkLabels, getInputLabel, GROUP_TABS, hasCreateEditInputError, ON_CHANGE_DELAY, SendEmailUser, TAB_LABEL_ID, transformToIdsFromListObject, TranslationsKeys, } from '../util';
import { ViewRolesPrivilegesModal } from '../view-roles-privileges-modal/view-roles-privilege-modal.container';
import styles from './create-edit-modal.module.scss';
const FORM_INPUT_ITEM = {
    displayName: 'displayName',
    name: 'name',
    description: 'description',
    allowOthersToShare: 'allowOthersToShare',
};
const initialWarningDialogue = {
    isOpen: false,
    title: '',
    description: '',
};
export const CreateEditForm = ({ groupData, message, isEditMode, isModalOpened, setShowError, closeModal, onSuccess, roleIds = [], userIds = [], groupListIds = [], preSelectedRoleIds = [], preSelectedUserIds = [], preSelectedGroupListIds = [], roleList = [], userGroupList = [], userList = [], filteredRoleList = [], filteredUserGroupList = [], filteredUserList = [], filterListData, onSendWelcomeEmail, onSendTestWelcomeEmail, setUserListInput, setUserGroupsListInput, setRoleListInput, setDefaultLiveboardInput, liveboardListIds = [], defaultLiveboardList = [], filteredDefaultLiveboardList = [], preSelectedLiveboardListIds = [], preDisabledLiveboardListIds = [], isUserListLoading, isGroupListLoading, isRoleListLoading, isLiveboardListLoading, allListData, preSelectedListGroupsInGroupRef, preSelectedListUsersInGroupRef, preSelectedRolesRef, preSelectedLiveboardsRef, }) => {
    const { t } = useTranslation();
    const getTranslations = (key, args) => t(`${key}`, args);
    const ignoredSelections = useRef(new Set());
    const isFilterLiveboardTouched = useRef(false);
    const rolesEnabled = isRolesEnabled();
    const formData = useRef({
        name: '',
        displayName: '',
        description: '',
        allowOthersToShare: true,
    });
    const [isReviewSelection, setIsReviewSelection] = useState(true);
    const [isError, setIsError] = useState(false);
    const [isViewRolesPrivilegesModalOpen, setIsViewRolesPrivilegesModalOpen,] = useState(false);
    const [errorMessage, setErrorMessage] = useState(message);
    const [warningDialogue, setWarningDialogue] = useState({
        ...initialWarningDialogue,
    });
    const [form, setForm] = useState({
        name: groupData?.getGroupDetailsById[0].name || '',
        displayName: groupData?.getGroupDetailsById[0].displayName || '',
        description: groupData?.getGroupDetailsById[0].description || '',
        allowOthersToShare: _.isUndefined(groupData?.getGroupDetailsById[0])
            ? true
            : groupData?.getGroupDetailsById[0].visibility ===
                SHARING_VISIBILITY.DEFAULT,
    });
    const [hasInputError, setHasInputError] = useState({
        isNameRequired: false,
        isDisplayNameRequired: false,
        isNameLong: false,
        isDisplayNameLong: false,
        isDescriptionLong: false,
    });
    const [untouchedFields, setUntouchedFields] = useState({
        isNameUntouched: true,
        isDisplayNameUntouched: true,
    });
    const [emailContent, setEmailContent] = useState({
        sendEmail: true,
        testSendEmail: true,
        message: '',
        emailSent: false,
        resend: false,
    });
    const [resendMailUser, setResendMailUser] = useState(SendEmailUser.NEW_USERS);
    const isTestEmailLabelEnabled = !!getUserEmail();
    const { checkedItems: checkedRoleItems, handleChange: handleRoleChange, setCheckedItems: setCheckedRoleItems, } = useCheckboxState(roleIds, preSelectedRoleIds);
    const { checkedItems: checkedUserItems, handleChange: handleUserChange, setCheckedItems: setCheckedUserItems, } = useCheckboxState(userIds, preSelectedUserIds);
    const { checkedItems: checkedGroupItems, handleChange: handleGroupChange, setCheckedItems: setCheckedGroupItems, } = useCheckboxState(groupListIds, preSelectedGroupListIds);
    const { checkedItems: checkedLiveboardItems, handleChange: handleLiveboardChange, setCheckedItems: setCheckedLiveboardItems, disabledItems: disabledLiveboardItems, disableCheckboxes: disableLiveboardCheckboxes, enableCheckboxes: enableLiveboardCheckboxes, } = useCheckboxState(liveboardListIds, preSelectedLiveboardListIds, preDisabledLiveboardListIds);
    const componentRefs = useRef([]);
    const generateRefs = useCallback(() => {
        const count = createEditTabLinkLabels(isRolesEnabled(), isEditMode)
            .length;
        componentRefs.current = Array(count)
            .fill(null)
            .map((_, i) => componentRefs.current[i] ?? createRef());
    }, []);
    const onConfirmWarningDialogue = useCallback(() => {
        setWarningDialogue({ ...initialWarningDialogue });
    }, [warningDialogue]);
    const handleInputChange = _.debounce((event) => {
        const { id, value } = event.target;
        if (FORM_INPUT_ITEM.name === id) {
            setUntouchedFields({
                ...untouchedFields,
                isNameUntouched: false,
            });
            setForm({
                ...form,
                name: value,
            });
        }
        if (FORM_INPUT_ITEM.description === id) {
            setForm({
                ...form,
                description: value,
            });
        }
        if (FORM_INPUT_ITEM.displayName === id) {
            setUntouchedFields({
                ...untouchedFields,
                isDisplayNameUntouched: false,
            });
            setForm({
                ...form,
                displayName: value,
            });
        }
        if (id === 'sendWelcomeEmail') {
            setEmailContent({
                ...emailContent,
                message: value,
            });
        }
    }, ON_CHANGE_DELAY);
    const handleCheckboxChange = (e, id) => {
        setForm({
            ...form,
            allowOthersToShare: !form.allowOthersToShare,
        });
    };
    const handleEmailUserChange = (e) => {
        const value = _.get(e, 'target.value');
        setEmailContent({
            ...emailContent,
            resend: value === SendEmailUser.ALL_USERS,
        });
        setResendMailUser(value);
    };
    useEffect(() => {
        if (!isError && !_.isEmpty(message)) {
            setIsError(true);
            setErrorMessage(message);
        }
    }, [message, isError]);
    useEffect(() => {
        setHasInputError(hasCreateEditInputError(hasInputError, form, untouchedFields));
    }, [form]);
    const onBannerClose = useCallback(() => {
        setShowError(null);
        setIsError(false);
        setErrorMessage(null);
    }, []);
    const submitForm = useCallback(() => {
        onBannerClose();
        const { isNameRequired, isDisplayNameRequired, isNameLong, isDisplayNameLong, isDescriptionLong, } = hasInputError;
        if (isNameRequired ||
            isNameLong ||
            isDisplayNameRequired ||
            isDisplayNameLong ||
            isDescriptionLong) {
            return;
        }
        const newFormData = {};
        const { name, description, displayName, allowOthersToShare } = form;
        newFormData.isRolesEnabled = isRolesEnabled();
        newFormData.groups = [...checkedGroupItems];
        newFormData.users = [...checkedUserItems];
        if (isEditMode) {
            const { id, userGroupContent, groupIdx, publishAcrossOrgs, metadataVersion, assignedPinboards, assignedRoles, granularPrivileges, assignedGroups, inheritedGroups, privileges, type, parenttype, tenantId, complete, incompleteDetail, isSystemPrincipal, header, } = groupData?.getGroupDetailsById[0];
            newFormData.groupid = id;
            if (newFormData.isRolesEnabled) {
                newFormData.roleIds = [...checkedRoleItems];
            }
            if (!isSaaSCluster() &&
                !groupData?.getGroupDetailsById[0].isSystemPrincipal) {
                newFormData.liveboards = [...checkedLiveboardItems];
            }
            newFormData.content = {
                userGroupContent,
                groupIdx,
                publishAcrossOrgs,
                metadataVersion,
                assignedPinboards,
                assignedRoles: newFormData.isRolesEnabled
                    ? [...checkedRoleItems]
                    : assignedRoles,
                granularPrivileges,
                assignedGroups,
                inheritedGroups,
                privileges: newFormData.isRolesEnabled
                    ? privileges
                    : [...checkedRoleItems],
                type,
                parenttype,
                visibility: allowOthersToShare
                    ? SHARING_VISIBILITY.DEFAULT
                    : SHARING_VISIBILITY.NON_SHARABLE,
                tenantId,
                displayName,
                header: {
                    id,
                    indexVersion: header.indexVersion,
                    generationNum: header.generationNum,
                    name,
                    displayName,
                    description,
                    author: header.author,
                    created: header.created,
                    modified: header.modified,
                    modifiedBy: header.modifiedBy,
                    owner: header.owner,
                    isDeleted: header.isDeleted,
                    isHidden: header.isHidden,
                    orgIds: header.orgIds,
                    tags: header.tags,
                    type: header.type,
                    aiAnswerGenerationDisabled: header.aiAnswerGenerationDisabled,
                    isExternal: header.isExternal,
                    isDeprecated: header.isDeprecated,
                    isSharedViaConnection: header.isSharedViaConnection,
                    contentUpgradeId: header.contentUpgradeId,
                    headerUpgradeId: header.headerUpgradeId,
                },
                complete,
                incompleteDetail,
                isSystemPrincipal,
            };
        }
        else {
            newFormData.name = name;
            newFormData.description = description;
            newFormData.displayname = displayName;
            newFormData.visibility = allowOthersToShare
                ? SHARING_VISIBILITY.DEFAULT
                : SHARING_VISIBILITY.NON_SHARABLE;
            if (newFormData.isRolesEnabled) {
                newFormData.roles = [...checkedRoleItems];
            }
            else {
                newFormData.privileges = [...checkedRoleItems];
            }
        }
        ignoredSelections.current.clear();
        onSuccess(newFormData);
    }, [
        form,
        checkedRoleItems,
        checkedGroupItems,
        checkedUserItems,
        checkedLiveboardItems,
        hasInputError,
    ]);
    const onDismissModal = () => {
        ignoredSelections.current.clear();
        closeModal();
    };
    const onConfirmModal = () => {
        submitForm();
    };
    const onReviewSelection = () => {
        const updatedUntouchedFields = {
            isNameUntouched: false,
            isDisplayNameUntouched: false,
        };
        setUntouchedFields(updatedUntouchedFields);
        formData.current = {
            name: '',
            displayName: '',
            description: '',
            allowOthersToShare: true,
        };
        const { name, displayName, description, allowOthersToShare } = form;
        if (name) {
            formData.current.name = name;
        }
        if (displayName) {
            formData.current.displayName = displayName;
        }
        if (description) {
            formData.current.description = description;
        }
        formData.current.allowOthersToShare = allowOthersToShare;
        const { isNameRequired, isDisplayNameRequired, isDisplayNameLong, isDescriptionLong, isNameLong, } = hasInputError;
        const hasError = !name ||
            !displayName ||
            isNameRequired ||
            isDisplayNameRequired ||
            isDisplayNameLong ||
            isDescriptionLong ||
            isNameLong;
        if (hasError) {
            setHasInputError(hasCreateEditInputError(hasInputError, form, updatedUntouchedFields));
            componentRefs.current[0].current.scrollIntoView({
                behavior: 'smooth',
            });
            return;
        }
        if (isError || !_.isEmpty(errorMessage)) {
            setIsError(false);
            setErrorMessage(null);
        }
        setIsReviewSelection(false);
    };
    const onBackModal = () => {
        setIsReviewSelection(true);
    };
    const getReviewSelectionOrConfirmButton = () => {
        if (isReviewSelection) {
            return {
                text: t(TranslationsKeys.REVIEW_SELECTION),
                onConfirm: onReviewSelection,
            };
        }
        return {
            text: t(TranslationsKeys.SAVE),
            onConfirm: onConfirmModal,
        };
    };
    const getModalTitle = (title) => {
        if (!isReviewSelection) {
            return t(TranslationsKeys.REVIEW_SELECTION);
        }
        return t(TranslationsKeys[title]);
    };
    const modalProps = {
        size: ModalSize.MEDIUM,
        title: isEditMode
            ? getModalTitle('EDIT_MODAL_TITLE')
            : getModalTitle('CREATE_MODAL_TITLE'),
        onDismiss: onDismissModal,
        isOpen: isModalOpened,
        confirmButton: getReviewSelectionOrConfirmButton(),
        denyButton: !isReviewSelection
            ? {
                text: t(TranslationsKeys.CANCEL),
                onDeny: onDismissModal,
            }
            : null,
        dismissButton: !isReviewSelection
            ? {
                text: t(TranslationsKeys.BACK),
                onClick: onBackModal,
            }
            : {
                text: t(TranslationsKeys.CANCEL),
            },
        className: styles.modalProps,
        contentClassName: styles.modalContentClassName,
        customHeaderClassName: styles.customHeader,
        customFooterClassName: styles.customFooter,
        autoScrollContent: false,
    };
    const scrollToComponent = (ref) => {
        ref.current.scrollIntoView({ behavior: 'smooth' });
    };
    const renderTabLinkComponent = (ref, index, records) => {
        if (!((index === 0 || index === 1) &&
            groupData?.getGroupDetailsById[0].id === getAdminGroupGuid())) {
            return (React.createElement(Horizontal, { key: records[index], className: styles.leftPanelHeader, vAlignContent: "center" },
                React.createElement("button", { onClick: () => scrollToComponent(ref), onKeyDown: e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            scrollToComponent(ref);
                        }
                    }, type: "button", className: styles.leftPanelButton },
                    React.createElement(Typography, { variant: "p", overrideTag: "div", "data-testid": `headerLink-${records[index]}` }, getTranslations(`groups.modalTabLinkLabel.${records[index]}`)))));
        }
        return null;
    };
    const renderTabLinks = (componentRefs, records) => {
        return (React.createElement(Vertical, { className: styles.leftPanel }, componentRefs.map((ref, index) => renderTabLinkComponent(ref, index, records))));
    };
    const showSelectedByGroup = (checked, data, checkedItems, tab) => {
        const filterChecked = data.filter((item) => checkedItems.has(item.id));
        if (!filterChecked.length || data.length === filterChecked.length) {
            return;
        }
        const updatedData = checked ? [...filterChecked] : [...data];
        filterListData(tab, updatedData);
    };
    const selectAllOrClearByGroup = (data, isClear, setCheckedItems, checkedItems, tab) => {
        const objRef = {
            [TAB_LABEL_ID.SELECT_ROLES]: preSelectedRolesRef,
            [TAB_LABEL_ID.SELECT_CHILD_GROUPS]: preSelectedListGroupsInGroupRef,
            [TAB_LABEL_ID.SELECT_USERS]: preSelectedListUsersInGroupRef,
            [TAB_LABEL_ID.MANAGE_LIVEBOARD_TAB]: preSelectedLiveboardsRef,
        };
        const selectableData = data.filter((record) => !ignoredSelections.current.has(record.id));
        if (isClear) {
            const filteredData = selectableData.filter((item) => checkedItems.has(item.id));
            if (filteredData.length !== selectableData.length) {
                showSelectedByGroup(false, selectableData, checkedItems, tab);
            }
        }
        setCheckedItems((prevState) => {
            const newState = new Set(prevState);
            selectableData.forEach((item) => {
                if (isClear) {
                    newState.delete(item.id);
                    if (objRef[tab]?.current) {
                        objRef[tab].current = objRef[tab].current.filter((record) => record.id !== item.id);
                    }
                }
                else {
                    newState.add(item.id);
                    if (objRef[tab]?.current) {
                        const isExists = objRef[tab].current.some((record) => record.id === item.id);
                        if (!isExists) {
                            objRef[tab].current.push(item);
                        }
                    }
                    if (objRef[tab] && !objRef[tab].current) {
                        objRef[tab].current = [item];
                    }
                }
            });
            return newState;
        });
    };
    const renderFormContent = (index) => {
        return (React.createElement(React.Fragment, null,
            React.createElement(Typography, { variant: "content-label-subhead", overrideTag: "div", className: styles.descContentTitle }, t(TranslationsKeys.TAB_DESCRIBE_GROUP)),
            React.createElement("div", null,
                React.createElement(Typography, { variant: "p", className: styles.formFieldLabelBottomMargin, overrideTag: "div", color: (hasInputError.isDisplayNameRequired ||
                        hasInputError.isDisplayNameLong) &&
                        Colors.failure }, getLabelWithOrWithOutErrorIcon(styles.labelErrorWithIcon, hasInputError.isDisplayNameRequired ||
                    hasInputError.isDisplayNameLong, getInputLabel(hasInputError, t, true))),
                React.createElement(Input.Text, { ariaLabel: t(TranslationsKeys.DISPLAY_NAME), value: form.displayName, hasError: hasInputError.isDisplayNameRequired ||
                        hasInputError.isDisplayNameLong, id: FORM_INPUT_ITEM.displayName, "data-testid": FORM_INPUT_ITEM.displayName, className: cx(styles.formFieldBottomMargin, styles.formFieldBottomPadding), onChange: handleInputChange }),
                React.createElement(Typography, { variant: "p", className: styles.formFieldLabelBottomMargin, overrideTag: "div", color: (hasInputError.isNameRequired ||
                        hasInputError.isNameLong ||
                        hasInputError.isDescriptionLong) &&
                        Colors.failure }, getLabelWithOrWithOutErrorIcon(styles.labelErrorWithIcon, hasInputError.isNameRequired ||
                    hasInputError.isNameLong ||
                    hasInputError.isDescriptionLong, getInputLabel(hasInputError, t))),
                React.createElement(Input.Text, { ariaLabel: t(TranslationsKeys.FIELDS_GROUP_NAME_DESC), value: form.name, hasError: hasInputError.isNameRequired ||
                        hasInputError.isNameLong, id: FORM_INPUT_ITEM.name, "data-testid": FORM_INPUT_ITEM.name, className: cx(styles.formFieldWithTextareaBottomPadding), onChange: handleInputChange }),
                React.createElement(TextArea, { ariaLabel: t(TranslationsKeys.FIELDS_GROUP_NAME_DESC), value: form.description, id: FORM_INPUT_ITEM.description, onChange: handleInputChange, hasError: hasInputError.isDescriptionLong, className: styles.formFieldBottomPadding, "data-testid": FORM_INPUT_ITEM.description }),
                React.createElement(Checkbox, { id: FORM_INPUT_ITEM.allowOthersToShare, label: t(TranslationsKeys.FIELDS_ALLOW_OTHERS_TO_SHARE), checked: form.allowOthersToShare, onChange: handleCheckboxChange, showTooltipOnEllipsis: true, tooltipContent: t(TranslationsKeys.FIELDS_ALLOW_OTHERS_TO_SHARE) }))));
    };
    const onEmailSendButtonClick = () => {
        setEmailContent({
            ...emailContent,
            sendEmail: false,
        });
        const id = groupData?.getGroupDetailsById[0].id;
        onSendWelcomeEmail(id, emailContent.message, emailContent.resend);
    };
    const onTestEmailSendButtonClick = () => {
        if (isTestEmailLabelEnabled && emailContent.testSendEmail) {
            setEmailContent({
                ...emailContent,
                testSendEmail: false,
            });
            const id = getUserGuid();
            onSendTestWelcomeEmail(id, '');
        }
    };
    const renderDefaultLiveboards = (index, selectedCountLabel, listData, originalListData, setCheckedItems, checkedItems, handleChange, readOnlyPredicate, isSearchIncluded = false, setSearchTerm, isListLoading = true) => {
        const liveBoardContent = (content) => {
            return (React.createElement(Vertical, { spacing: "a", className: styles.checkboxListTopContainer },
                React.createElement(Horizontal, { className: styles.contentHeading }, getTranslations(`groups.modalTabLinkLabel.${createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index]}`)),
                React.createElement(Horizontal, null,
                    React.createElement(SafeHTML, { html: t(TranslationsKeys[content]) }))));
        };
        const content = isEditMode
            ? 'ADMIN_DEFAULT_LIVEBOARD_INTRODUCTION_ERROR'
            : 'NEW_DEFAULT_LIVEBOARD_INTRODUCTION_1';
        if (!isEditMode ||
            groupData?.getGroupDetailsById[0].isSystemPrincipal) {
            return liveBoardContent(content);
        }
        if (!listData.length && !isFilterLiveboardTouched.current) {
            return liveBoardContent('DEFAULT_LIVEBOARD_MESSAGE');
        }
        const isSelectAllCheckboxChecked = listData.length > 0 &&
            listData.every((data) => checkedItems.has(data.id));
        return (React.createElement(React.Fragment, null,
            React.createElement(Vertical, { spacing: "a", className: styles.checkboxListTopContainer },
                React.createElement(Horizontal, { className: styles.contentHeading }, getTranslations(`groups.modalTabLinkLabel.${createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index]}`)),
                isSearchIncluded && (React.createElement(SearchBar, { searchValue: "", searchPlaceholder: t('cirrus.metadata.search.placeHolder'), onSearchChange: (searchTerm) => {
                        isFilterLiveboardTouched.current = true;
                        setSearchTerm(searchTerm);
                    }, "data-testid": "liveboard-search-bar-filters", className: styles.checkboxContainerSearchBox })),
                React.createElement(Horizontal, { className: styles.countFilterContainer },
                    React.createElement(Horizontal, null, `${t(TranslationsKeys[selectedCountLabel])} (${checkedItems.size})`))),
            React.createElement(Vertical, { className: styles.checkboxListContainer },
                React.createElement(LoadingIndicator.Global, { hideLoader: !isListLoading },
                    React.createElement(Vertical, { className: styles.checkboxList }, listData.map((item) => (React.createElement(Checkbox, { id: item.id, key: item.id, label: item.name, checked: checkedItems.has(item.id), isList: true, onChange: e => {
                            if (checkedItems.size === 2 &&
                                e.target
                                    .checked) {
                                const toBeHidden = transformToIdsFromListObject(liveboardListIds, [...checkedItems, item.id]);
                                disableLiveboardCheckboxes(...toBeHidden);
                            }
                            else if (checkedItems.size >= 3) {
                                enableLiveboardCheckboxes(...disabledLiveboardItems);
                            }
                            handleChange(e, item);
                        }, disabled: disabledLiveboardItems.has(item.id), showTooltipOnEllipsis: true, tooltipContent: item.name }))))),
                React.createElement(Vertical, { className: styles.checkboxListShowSelectedContainer },
                    React.createElement(Toggle, { checked: isSelectAllCheckboxChecked, onChange: (e) => {
                            showSelectedByGroup(e.target.checked, originalListData, checkedItems, createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index]);
                        }, labelPosition: "right", "data-testid": `showSelected-${createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index]}`, label: t(TranslationsKeys.SHOW_SELECTED) })))));
    };
    const onViewRolesPrivilegesButtonClick = useCallback(() => {
        setIsViewRolesPrivilegesModalOpen(true);
    }, [isViewRolesPrivilegesModalOpen]);
    const onViewRolesPrivilegesClose = useCallback(() => {
        setIsViewRolesPrivilegesModalOpen(false);
    }, [isViewRolesPrivilegesModalOpen]);
    const renderCheckBoxesList = (index, selectedCountLabel, listData, originalListData, setCheckedItems, checkedItems, handleChange, readOnlyPredicate, isSearchIncluded = false, setSearchTerm, isListLoading = true) => {
        const isSelectAllCheckboxChecked = listData.length > 0 &&
            listData.every((data) => checkedItems.has(data.id));
        return (React.createElement(React.Fragment, null,
            React.createElement(Vertical, { spacing: "a", className: styles.checkboxListTopContainer },
                React.createElement(Horizontal, { className: styles.contentHeading }, getTranslations(`groups.modalTabLinkLabel.${createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index]}`)),
                isSearchIncluded && (React.createElement(SearchBar, { searchValue: "", searchPlaceholder: t('cirrus.metadata.search.placeHolder'), onSearchChange: (searchTerm) => {
                        setSearchTerm(searchTerm);
                    }, "data-testid": "search-bar-filters", className: styles.checkboxContainerSearchBox })),
                React.createElement(Horizontal, { className: styles.countFilterContainer },
                    React.createElement(Horizontal, null, `${t(TranslationsKeys[selectedCountLabel])} (${checkedItems.size})`),
                    React.createElement(Horizontal, { hAlignContent: "right" },
                        React.createElement(Horizontal, { tabIndex: 0, onKeyDown: event => {
                                if (event.key === 'Enter') {
                                    selectAllOrClearByGroup(originalListData, false, setCheckedItems, checkedItems, createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index]);
                                }
                            } },
                            React.createElement(Typography, { variant: "p", overrideTag: "div", color: Colors.accent, className: styles.headerActionLink, onClick: () => {
                                    selectAllOrClearByGroup(originalListData, false, setCheckedItems, checkedItems, createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index]);
                                } }, t(TranslationsKeys.SELECT_ALL))),
                        React.createElement(Divider, { vertical: true, className: styles.headerActionLinkDivider }),
                        React.createElement(Horizontal, { tabIndex: 0, onKeyDown: event => {
                                if (event.key === 'Enter') {
                                    selectAllOrClearByGroup(originalListData, true, setCheckedItems, checkedItems, createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index]);
                                }
                            } },
                            React.createElement(Typography, { variant: "p", overrideTag: "div", color: Colors.accent, className: styles.headerActionLink, onClick: () => {
                                    selectAllOrClearByGroup(originalListData, true, setCheckedItems, checkedItems, createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index]);
                                } }, t(TranslationsKeys.CLEAR)))))),
            React.createElement(Vertical, { className: styles.checkboxListContainer },
                React.createElement(LoadingIndicator.Global, { hideLoader: !isListLoading },
                    React.createElement(Vertical, { className: styles.checkboxList }, listData.map((item) => (React.createElement(Checkbox, { id: item.id, key: item.id, label: item.name, checked: checkedItems.has(item.id), isList: true, onChange: e => {
                            if (item?.hasWarningMessageToShow &&
                                e.target
                                    .checked) {
                                setWarningDialogue({
                                    isOpen: true,
                                    title: item?.warningTitle,
                                    description: item?.warningDescription,
                                });
                            }
                            handleChange(e, item);
                        }, disabled: readOnlyPredicate &&
                            readOnlyPredicate(item.id), showTooltipOnEllipsis: true, tooltipContent: item.name }))))),
                React.createElement(Vertical, { className: styles.checkboxListShowSelectedContainer },
                    React.createElement(Toggle, { checked: isSelectAllCheckboxChecked, onChange: (e) => {
                            showSelectedByGroup(e.target.checked, originalListData, checkedItems, createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index]);
                        }, labelPosition: "right", "data-testid": `showSelected-${createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index]}`, label: t(TranslationsKeys.SHOW_SELECTED) }))),
            index === 1 && isRolesEnabled() && (React.createElement(Vertical, null,
                React.createElement(Horizontal, { className: styles.viewRolesPrivileges, tabIndex: 0, onKeyDown: event => {
                        if (event.key === 'Enter') {
                            onViewRolesPrivilegesButtonClick();
                        }
                    } },
                    React.createElement(Typography, { variant: "p", overrideTag: "div", color: Colors.accent, className: styles.ViewRolesPrivilegesActionLink, onClick: onViewRolesPrivilegesButtonClick }, t(TranslationsKeys.VIEW_ALL_ROLES_PRIVILEGES)))))));
    };
    const renderSendEmailContent = (index) => {
        if (isEditMode) {
            return (React.createElement(React.Fragment, null,
                React.createElement(Horizontal, { spacing: "c", className: styles.checkboxListTopContainer },
                    React.createElement(Typography, { variant: "content-label-subhead", overrideTag: "div", className: styles.descContentTitle }, getTranslations(`groups.modalTabLinkLabel.${createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index]}`))),
                React.createElement(Horizontal, null, emailContent.sendEmail ? (React.createElement(Typography, { variant: "p", className: styles.welcomeEmailExplainer }, t(TranslationsKeys.RESEND_WELCOME_EMAIL_EXPLAINER))) : (React.createElement(React.Fragment, null))),
                React.createElement(Horizontal, null, emailContent.sendEmail ? (React.createElement(Radio.Group, { id: "welcomeEmailUserSelection", selectedValue: resendMailUser, onChange: handleEmailUserChange, orientation: "tray" },
                    React.createElement(Radio, { id: SendEmailUser.ALL_USERS, label: t(TranslationsKeys.ALL_USERS), value: SendEmailUser.ALL_USERS }),
                    React.createElement(Radio, { id: SendEmailUser.NEW_USERS, label: t(TranslationsKeys.NEW_USERS), value: SendEmailUser.NEW_USERS }))) : (React.createElement(React.Fragment, null))),
                React.createElement("div", null,
                    emailContent.sendEmail ? (React.createElement(React.Fragment, null,
                        React.createElement(TextArea, { ariaLabel: "resend welcome email", value: emailContent.message, id: "sendWelcomeEmail", "data-testid": "sendWelcomeEmail", className: styles.formFieldBottomPadding, onChange: handleInputChange, placeholder: t(TranslationsKeys.SEND_MESSAGE_PLACEHOLDER) }),
                        React.createElement(Button, { text: t(TranslationsKeys.SEND), "data-testid": "edit-email-send-btn", onClick: onEmailSendButtonClick }))) : (React.createElement(Typography, { variant: "p" }, t(TranslationsKeys.EMAIL_RESEND_CONFIRMATION))),
                    isWelcomeEmailEnabled() &&
                        emailContent.testSendEmail ? (React.createElement(Horizontal, { className: styles.testWelcomeEmailCls },
                        React.createElement(Typography, { variant: "p", overrideTag: "div", color: isTestEmailLabelEnabled
                                ? Colors.accent
                                : Colors.base, className: styles.testEmailHeaderActionLink, onClick: onTestEmailSendButtonClick }, t(TranslationsKeys.TEST_WELCOME_EMAIL)))) : null,
                    !emailContent.testSendEmail ? (React.createElement(Typography, { variant: "p", className: styles.testWelcomeEmailCls }, t(TranslationsKeys.ADMIN_EMAIL_SENT_CONFIRMATION))) : null)));
        }
        return React.createElement(React.Fragment, null);
    };
    const renderTabBody = (tabs, index) => {
        const updateCaseIndex = (numIndex) => {
            if (!isSaaSCluster()) {
                return numIndex;
            }
            if (isSaaSCluster() && numIndex === 2) {
                return 22;
            }
            return numIndex - 1;
        };
        switch (tabs) {
            case createEditTabLinkLabels(isRolesEnabled(), isEditMode)[0]:
                return renderFormContent(index);
            case createEditTabLinkLabels(isRolesEnabled(), isEditMode)[1]:
                return renderCheckBoxesList(index, rolesEnabled ? 'ROLES_TITLE' : 'PRIVILEGES_LABEL', filteredRoleList, roleList, setCheckedRoleItems, checkedRoleItems, (e, item) => {
                    const isChecked = e.target
                        .checked;
                    if (isChecked && preSelectedRolesRef.current) {
                        preSelectedRolesRef.current.push(item);
                    }
                    if (isChecked &&
                        !isEditMode &&
                        !preSelectedRolesRef.current) {
                        preSelectedRolesRef.current = [item];
                    }
                    if (!isChecked && preSelectedRolesRef.current) {
                        preSelectedRolesRef.current = preSelectedRolesRef.current.filter((record) => record.id !== item.id);
                    }
                    handleRoleChange(e, item.id);
                }, null, rolesEnabled, (text) => {
                    setRoleListInput(text);
                }, isRoleListLoading);
            case createEditTabLinkLabels(isRolesEnabled(), isEditMode)[updateCaseIndex(2)]:
                return renderDefaultLiveboards(index, 'TAB_SELECT_DEFAULT_LIVEBOARDS', filteredDefaultLiveboardList, defaultLiveboardList, setCheckedLiveboardItems, checkedLiveboardItems, (e, item) => {
                    const isChecked = e.target
                        .checked;
                    if (isChecked && preSelectedLiveboardsRef.current) {
                        preSelectedLiveboardsRef.current.push(item);
                    }
                    if (isChecked &&
                        !isEditMode &&
                        !preSelectedLiveboardsRef.current) {
                        preSelectedLiveboardsRef.current = [item];
                    }
                    if (!isChecked && preSelectedLiveboardsRef.current) {
                        preSelectedLiveboardsRef.current = preSelectedLiveboardsRef.current.filter((record) => record.id !== item.id);
                    }
                    handleLiveboardChange(e, item.id);
                }, null, true, (text) => {
                    setDefaultLiveboardInput(text);
                }, isLiveboardListLoading);
            case createEditTabLinkLabels(isRolesEnabled(), isEditMode)[updateCaseIndex(3)]:
                return renderCheckBoxesList(index, 'CHILD_GROUPS', filteredUserGroupList, userGroupList, setCheckedGroupItems, checkedGroupItems, (e, item) => {
                    const isChecked = e.target
                        .checked;
                    if (isChecked &&
                        preSelectedListGroupsInGroupRef.current) {
                        preSelectedListGroupsInGroupRef.current.push(item);
                    }
                    if (isChecked &&
                        !isEditMode &&
                        !preSelectedListGroupsInGroupRef.current) {
                        preSelectedListGroupsInGroupRef.current = [item];
                    }
                    if (!isChecked &&
                        preSelectedListGroupsInGroupRef.current) {
                        preSelectedListGroupsInGroupRef.current = preSelectedListGroupsInGroupRef.current.filter((record) => record.id !== item.id);
                    }
                    handleGroupChange(e, item.id);
                }, (id) => {
                    const isReadOnly = id === groupData?.getGroupDetailsById[0].id;
                    if (isReadOnly && !ignoredSelections.current.has(id)) {
                        ignoredSelections.current.add(id);
                    }
                    return isReadOnly;
                }, true, (text) => {
                    setUserGroupsListInput(text);
                }, isGroupListLoading);
            case createEditTabLinkLabels(isRolesEnabled(), isEditMode)[updateCaseIndex(4)]:
                return renderCheckBoxesList(index, 'MEMBER_USERS', filteredUserList, userList, setCheckedUserItems, checkedUserItems, (e, item) => {
                    const isChecked = e.target
                        .checked;
                    if (isChecked &&
                        preSelectedListUsersInGroupRef.current) {
                        preSelectedListUsersInGroupRef.current.push(item);
                    }
                    if (isChecked &&
                        !isEditMode &&
                        !preSelectedListUsersInGroupRef.current) {
                        preSelectedListUsersInGroupRef.current = [item];
                    }
                    if (!isChecked &&
                        preSelectedListUsersInGroupRef.current) {
                        preSelectedListUsersInGroupRef.current = preSelectedListUsersInGroupRef.current.filter((record) => record.id !== item.id);
                    }
                    handleUserChange(e, item.id);
                }, (id) => {
                    const isReadOnly = (groupData?.getGroupDetailsById[0]
                        .isSystemPrincipal &&
                        id === getUserGuid()) ||
                        id === getTSAdminUserGuid();
                    if (isReadOnly && !ignoredSelections.current.has(id)) {
                        ignoredSelections.current.add(id);
                    }
                    return isReadOnly;
                }, true, (text) => {
                    setUserListInput(text);
                }, isUserListLoading);
            case createEditTabLinkLabels(isRolesEnabled(), isEditMode)[updateCaseIndex(5)]:
                if (!isEditMode)
                    return React.createElement(React.Fragment, null);
                return renderSendEmailContent(index);
            default:
                return React.createElement(React.Fragment, null);
        }
    };
    const renderTabContent = (ref, index) => {
        return (React.createElement("div", { ref: ref, className: styles.rightPanelItem, id: `section-${createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index]}` }, renderTabBody(createEditTabLinkLabels(isRolesEnabled(), isEditMode)[index], index)));
    };
    const renderTabComponent = (ref, index) => {
        if (!((index === 0 || index === 1) &&
            groupData?.getGroupDetailsById[0].id === getAdminGroupGuid()))
            return (React.createElement(Fragment, { key: `componentRefs_${index}` },
                renderTabContent(ref, index),
                index !== componentRefs.current.length - 1 && (React.createElement(Divider, { className: styles.rightPanelDivider }))));
        return null;
    };
    const renderReviewScreen = () => {
        if (!isReviewSelection) {
            const sharingVisibility = formData.current.allowOthersToShare
                ? SHARING_VISIBILITY.DEFAULT
                : SHARING_VISIBILITY.NON_SHARABLE;
            const grpData = groupData?.getGroupDetailsById[0];
            let isSharingViewDeleted = sharingVisibility !== SHARING_VISIBILITY.DEFAULT;
            if (isEditMode && !_.isUndefined(grpData)) {
                isSharingViewDeleted =
                    grpData?.visibility !== sharingVisibility
                        ? sharingVisibility !== SHARING_VISIBILITY.DEFAULT
                        : false;
            }
            return (React.createElement(Vertical, { className: styles.reviewScreen },
                renderFormFieldValue(t(TranslationsKeys.DISPLAY_NAME), grpData?.displayName !== formData.current.displayName, styles.reviewName, formData.current.displayName, true, false, styles),
                renderFormFieldValue(t(TranslationsKeys.FIELDS_GROUP_NAME_DESC), grpData?.name !== formData.current.name, styles.reviewName, formData.current.name, true, false, styles),
                formData.current.description &&
                    renderFormFieldValue(t(TranslationsKeys.FIELDS_GROUP_NAME_DESC), grpData?.description !==
                        formData.current.description, styles.reviewName, formData.current.description, false, false, styles),
                renderFormFieldValue(t(TranslationsKeys.SHARING), (isEditMode &&
                    grpData?.visibility !== sharingVisibility) ||
                    grpData?.visibility !== sharingVisibility, styles.reviewName, t(TranslationsKeys.FIELDS_ALLOW_OTHERS_TO_SHARE), true, isSharingViewDeleted, styles),
                checkedRoleItems.size ||
                    (isEditMode && preSelectedRoleIds.length)
                    ? renderCheckboxListReview(isRolesEnabled()
                        ? t(TranslationsKeys.ROLES_TITLE)
                        : t(TranslationsKeys.PRIVILEGES_LABEL), preSelectedRoleIds, isRolesEnabled()
                        ? allListData.current[GROUP_TABS.ROLES]
                        : roleList, checkedRoleItems, styles, isEditMode)
                    : null,
                !isSaaSCluster() &&
                    (checkedLiveboardItems.size ||
                        (isEditMode && preSelectedLiveboardListIds.length))
                    ? renderCheckboxListReview(t(TranslationsKeys.TAB_SELECT_MANAGE_LIVEBOARDS), preSelectedLiveboardListIds, allListData.current[GROUP_TABS.LIVEBOARDS], checkedLiveboardItems, styles, isEditMode)
                    : null,
                checkedGroupItems.size ||
                    (isEditMode && preSelectedGroupListIds.length)
                    ? renderCheckboxListReview(t(TranslationsKeys.CHILD_GROUP), preSelectedGroupListIds, allListData.current[GROUP_TABS.GROUPS], checkedGroupItems, styles, isEditMode)
                    : null,
                checkedUserItems.size ||
                    (isEditMode && preSelectedUserIds.length)
                    ? renderCheckboxListReview(t(TranslationsKeys.MEMBER_USERS), preSelectedUserIds, allListData.current[GROUP_TABS.USERS], checkedUserItems, styles, isEditMode)
                    : null));
        }
        return null;
    };
    generateRefs();
    return (React.createElement("div", null,
        React.createElement(Modal, { ...modalProps },
            React.createElement(Vertical, { className: styles.modalContent, hAlignContent: "stretch", grow: true, "data-testid": "group-create-edit-container-modal" },
                React.createElement(Alert.Banner, { message: errorMessage, isVisible: isError, type: BannerAlertType.ERROR, className: styles.alertBanner, onClose: onBannerClose }),
                React.createElement("div", { "data-testid": "groupModal_createEdit" },
                    React.createElement(Horizontal, { className: cx(styles.mainContent, {
                            [styles.hideAddEdit]: !isReviewSelection,
                        }) },
                        React.createElement(View, { hAlignContent: "left", className: styles.viewContent },
                            renderTabLinks(componentRefs.current, createEditTabLinkLabels(isRolesEnabled(), isEditMode)),
                            React.createElement(Vertical, { className: styles.rightPanel }, componentRefs.current.map((ref, index) => renderTabComponent(ref, index))))),
                    !isReviewSelection ? (React.createElement(Horizontal, { className: cx(styles.mainReviewContent), "data-testid": "review-selection-screen" }, renderReviewScreen())) : null),
                React.createElement(Alert.Dialogue, { ...warningDialogue, primaryButton: {
                        text: t(TranslationsKeys.MODAL_OK),
                        onConfirm: onConfirmWarningDialogue,
                    }, onDismiss: onConfirmWarningDialogue }))),
        isViewRolesPrivilegesModalOpen && (React.createElement(ViewRolesPrivilegesModal, { closeModal: onViewRolesPrivilegesClose }))));
};
//# sourceMappingURL=create-edit-form.container.js.map